import { stringToBoolean } from '@elab/core/utils';

import { globalTypes } from '../types';
import { reduceAction } from '../helpers';

import types from './types';

export const initialState = () => ({
    searchValue: '',
    lastSearch: '',
    searchResults: {
        categories: [],
        content: [],
        products: [],
        baseUrlSuffix: '',
        stores: [],
        brands: [],
        suggestions: [],
        productsTotalNumberOfResults: '',
        static: {},
        noResultsMessage: '',
        hasResults: false,
        externalCatalogsMapping: {},
        hasExternalCatalogsMapping: false,
    },
    helpSearchResults: {
        popular: [],
        recent: [],
    },
    suggestionsReceived: false,
    secondaryResultsTotal: 0,
    APIResponse: false,
    filterLoading: false,
    suggestionsOpened: false,
    plp: {
        newUri: null,
        newDoc: null,
    },
    expandedFilters: {},
    expandedFilterAccordions: {},
    lastChangedFiltersGroup: '',
    filtersAreNotApplied: true,
    filtersValidated: true,
    searchFromCategories: false,
    selectedFacetGroups: {},
    selectedFilterPriceRange: {
        minPriceRange: '',
        maxPriceRange: '',
    },
    searchForExtService: {},
});

const reducers = (state = initialState(), action) => {
    switch (action.type) {
        case globalTypes.INITIALIZE:
            return initialState();

        case types.SET_VALUE:
            return reduceAction(state, 'searchValue', action.payload);

        case types.SET_INPUT_VALUE: {
            const { value: searchValue, minCharacters } = action.payload;
            const shouldClearResults = searchValue.length < minCharacters;

            return {
                ...state,
                searchValue,
                searchResults: shouldClearResults
                    ? { ...initialState().searchResults }
                    : { ...state.searchResults },
                APIResponse: shouldClearResults ? false : state.APIResponse,
            };
        }

        case types.SET_LAST_SEARCH:
            return reduceAction(state, 'lastSearch', action.payload);

        case types.SET_SEARCH_FROM_CATEGORIES:
            return reduceAction(state, 'searchFromCategories', action.payload);

        case types.FILTER_LOADING:
            return reduceAction(state, 'filterLoading', action.payload);

        case types.SET_CATEGORIES: {
            const searchResults = action.payload;
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    categories: searchResults,
                },
            };
        }

        case types.SET_CONTENT_SUGGESTIONS:
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    content: action.payload,
                },
            };

        case types.SET_STATIC_SUGGESTION:
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    static: action.payload,
                },
            };

        case types.SET_RECENT_SEARCH:
            return {
                ...state,
                helpSearchResults: {
                    ...state.helpSearchResults,
                    recent: action.payload,
                },
            };

        case types.SET_PRODUCTS: {
            const { searchResults, totalNumberOfResults } = action.payload;

            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    products: searchResults,
                    productsTotalNumberOfResults: totalNumberOfResults,
                },
            };
        }

        case types.SET_BASE_URL_SUFFIX: {
            const searchResults = action.payload;

            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    baseUrlSuffix: searchResults,
                },
            };
        }

        case types.SET_NO_RESULTS_MESSAGE:
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    noResultsMessage: action.payload,
                },
            };
        case types.SET_HAS_RESULTS:
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    hasResults: action.payload,
                },
            };

        case types.SET_STORES:
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    stores: action.payload || [],
                },
            };

        case types.SET_AUTO_SUGGESTIONS:
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    suggestions: action.payload || [],
                },
            };

        case types.SET_POPULAR_SUGGESTIONS:
            return {
                ...state,
                helpSearchResults: {
                    ...state.helpSearchResults,
                    popular: action.payload || [],
                },
            };

        case types.SET_BRAND_SUGGESTIONS:
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    brands: action.payload,
                },
            };

        case types.SET_HAS_EXTERNAL_CATALOGS_MAPPING:
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    hasExternalCatalogsMapping: action.payload,
                },
            };

        case types.SET_API_RESPONSE_SUCCEDED:
            return reduceAction(state, 'APIResponse', action.payload);

        case types.SET_NEW_DOC:
            return {
                ...state,
                plp: { ...action.payload },
            };

        case types.SET_FILTERS_ARE_APPLIED:
            return reduceAction(state, 'filtersAreNotApplied', !action.payload);

        case types.SET_FILTERS_VALIDATED:
            return reduceAction(state, 'filtersValidated', action.payload);

        case types.GET_SECONDARY_RESULTS_TOTAL:
            return reduceAction(state, 'secondaryResultsTotal', 0);

        case types.SET_SECONDARY_RESULTS_TOTAL:
            return reduceAction(state, 'secondaryResultsTotal', action.payload);

        case types.CHANGE_FILTER: {
            const { checked, groupUid, shouldExpand } = action.payload;
            const wasUpdated = Object.prototype.hasOwnProperty.call(
                state.expandedFilters,
                groupUid,
            );
            return {
                ...state,
                lastChangedFiltersGroup: groupUid,
                expandedFilters: state.expandedFilters[groupUid]
                    ? state.expandedFilters
                    : {
                          ...state.expandedFilters,
                          [groupUid]: wasUpdated && checked && shouldExpand,
                      },
            };
        }

        case types.SET_FILTER_GROUP_EXPANDED: {
            const { groupUid, expanded } = action.payload;
            const wasUpdated = Object.prototype.hasOwnProperty.call(
                state.expandedFilters,
                groupUid,
            );
            return !wasUpdated
                ? state
                : {
                      ...state,
                      expandedFilters: {
                          ...state.expandedFilters,
                          [groupUid]: expanded,
                      },
                  };
        }

        case types.CLEAR_FILTERS:
            return {
                ...state,
                expandedFilters: {},
                expandedFilterAccordions: {},
            };

        case types.SET_SUGGESTIONS_OPENED:
            return reduceAction(state, 'suggestionsOpened', action.payload);

        case types.SET_SUGGESTIONS_RECEIVED:
            return reduceAction(state, 'suggestionsReceived', action.payload);

        case types.CLEAR_LAST_CHANGED_FILTERS_GROUP:
            return reduceAction(state, 'lastChangedFiltersGroup', '');

        case types.SET_FILTER_ACCORDION_OPENED: {
            const { name, opened } = action.payload;

            return {
                ...state,
                expandedFilterAccordions: {
                    ...state.expandedFilterAccordions,
                    [name]: opened,
                },
            };
        }

        case types.ADD_SELECTED_FACET_GROUP:
            return {
                ...state,
                selectedFacetGroups: {
                    ...state.selectedFacetGroups,
                    [action.payload.groupId]: stringToBoolean(action.payload.value),
                },
            };

        case types.REMOVE_SELECTED_FACET_GROUPS:
            return { ...state, selectedFacetGroups: {} };

        case types.SET_FILTER_PRICE_RANGE:
            return reduceAction(state, 'selectedFilterPriceRange', action.payload);

        case types.SET_SEARCH_FOR_EXT_SERVICE: {
            const data = action.payload;
            const key = Object.keys(data)[0];
            const removeProp = key;
            const { [removeProp]: remove, ..._searchForExtService } = state.searchForExtService;
            const _searchForExtService_ = {
                ..._searchForExtService,
                [key]: data[key],
            };
            return {
                ...state,
                searchForExtService: _searchForExtService_,
            };
        }
        case types.SET_EXTERNAL_CATALOGS_MAPPING:
            return {
                ...state,
                searchResults: {
                    ...state.searchResults,
                    externalCatalogsMapping: action.payload,
                },
            };
        case types.SET_REMOVE_SEO_TEXT:
            return { ...state, removeSeoText: action.payload };

        default:
            return state;
    }
};

export default reducers;
